/**
 * AcenteCollectedPayments — Admin/operasyon-only ledger listing every
 * cash payment recorded by saha_calisani.
 *
 * Görev:
 *   • Üstte KPI şeridi: toplam alınan tutar, alınacak tutar, fark, kayıt sayısı.
 *   • Alt hemen: her saha çalışanı için tek satırlık özet ("kim ne kadar topladı").
 *   • Ana tablo: rezervasyon bazlı liste — grup, otel, saha çalışanı,
 *     zaman damgası, alınacak, alınan, fark, oda numaraları.
 *   • Filtreler: tarih aralığı + saha çalışanı + otel + arama.
 *   • Excel indirme (xlsx).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import panelApi from "@/lib/panelApi";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Wallet, TrendingUp, TrendingDown, Users, Search, Download,
  ArrowRight, Loader2, Calendar, Hotel, Phone,
} from "lucide-react";
import * as XLSX from "xlsx";

function fmtSar(n) {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}${Math.abs(v).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ﷼`;
}
function fmtStamp(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return ""; }
}

function KpiCard({ icon: Icon, label, value, sub, accent = "brand-navy", testid }) {
  return (
    <div className={`rounded-2xl bg-white border border-${accent}/10 shadow-sm p-4`} data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl bg-${accent}/10 text-${accent} flex items-center justify-center`}>
        <Icon size={18} />
      </div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2 font-semibold">{label}</div>
      <div className="font-serif text-2xl text-brand-navy mt-0.5">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function AcenteCollectedPayments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [collectorId, setCollectorId] = useState("all");
  const [hotelName, setHotelName] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const load = () => {
    setLoading(true);
    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (collectorId !== "all") params.saha_user_id = collectorId;
    if (hotelName !== "all") params.hotel_name = hotelName;
    panelApi.get("/collected-payments", { params })
      .then((r) => setData(r.data || null))
      .catch((e) => toast.error(e?.friendly || "Veri alınamadı"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const items = data?.items || [];

  // Fill collector + hotel dropdowns from all items (before filtering) so
  // switching to another filter never traps the admin in an empty combobox.
  const collectorOptions = useMemo(() => {
    const seen = new Map();
    (data?.items || []).forEach((it) => {
      if (it.collected_by && !seen.has(it.collected_by)) {
        seen.set(it.collected_by, it.collected_by_name || "—");
      }
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [data?.items]);

  const hotelOptions = useMemo(() => {
    const s = new Set();
    (data?.items || []).forEach((it) => it.hotel_name && s.add(it.hotel_name));
    return Array.from(s).sort();
  }, [data?.items]);

  const filtered = useMemo(() => {
    let out = [...items];
    const s = q.trim().toLowerCase();
    if (s) {
      out = out.filter((it) =>
        (it.reservation_number || "").toLowerCase().includes(s) ||
        (it.group_name || "").toLowerCase().includes(s) ||
        (it.hotel_name || "").toLowerCase().includes(s) ||
        (it.collected_by_name || "").toLowerCase().includes(s) ||
        (it.agency_name || "").toLowerCase().includes(s)
      );
    }
    return out;
  }, [items, q]);

  const filteredTotals = useMemo(() => {
    return filtered.reduce((a, it) => {
      a.collected += it.collected_amount || 0;
      a.expected += it.expected_fee || 0;
      return a;
    }, { collected: 0, expected: 0 });
  }, [filtered]);

  const exportXlsx = () => {
    const header = [
      "Tarih/Saat", "Rez.No", "Grup", "Otel", "Acente",
      "Saha Çalışanı", "Alınacak (﷼)", "Alınan (﷼)", "Fark (﷼)",
      "Oda No", "Müşteri Tel", "Giriş", "Çıkış", "Kişi",
    ];
    const rows = filtered.map((it) => [
      fmtStamp(it.collected_at),
      it.reservation_number, it.group_name || "", it.hotel_name || "", it.agency_name || "",
      it.collected_by_name || "",
      Number(it.expected_fee || 0),
      Number(it.collected_amount || 0),
      Number(it.delta || 0),
      it.room_numbers || "",
      it.customer_phone || "",
      it.checkin || "", it.checkout || "",
      (it.adults || 0) + (it.children || 0) + (it.staff || 0),
    ]);
    // Totals row
    rows.push([
      "TOPLAM", "", "", "", "", "",
      Math.round(filteredTotals.expected),
      Math.round(filteredTotals.collected),
      Math.round(filteredTotals.collected - filteredTotals.expected),
      "", "", "", "", "",
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    ws["!cols"] = header.map((h) => ({ wch: Math.max(h.length + 2, 12) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Toplanan Ücretler");
    XLSX.writeFile(wb, `toplanan-ucretler-${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const deltaClass = (n) => n > 0 ? "text-emerald-700" : n < 0 ? "text-rose-600" : "text-slate-500";

  return (
    <div className="space-y-5" data-testid="collected-payments">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2 inline-flex items-center gap-1">
            <Wallet size={12} /> Toplanan Ücretler
          </div>
          <h1 className="font-serif text-3xl text-brand-navy">Saha Tahsilatı</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Saha çalışanlarınızın müşterilerden fiziksel olarak topladığı tüm ödemeler burada listelenir.
            Alınan tutar ve alınacak ücret karşılaştırılarak eksik/fazla tahsilatlar hemen görülebilir.
          </p>
        </div>
        <Button variant="outline" onClick={exportXlsx} data-testid="collected-export" disabled={filtered.length === 0}>
          <Download size={14} className="mr-1" /> Excel'e İndir
        </Button>
      </header>

      {/* KPI strip */}
      <div className="grid gap-3 sm:grid-cols-4" data-testid="collected-kpis">
        <KpiCard
          icon={Wallet}
          label="Toplam Alınan"
          value={fmtSar(data?.total_collected)}
          sub={`${data?.total_count || 0} kayıt`}
          accent="emerald-600"
          testid="kpi-collected"
        />
        <KpiCard
          icon={TrendingUp}
          label="Alınacak Ücret"
          value={fmtSar(data?.total_expected)}
          sub="Rezervasyonlardaki toplam tutar"
          accent="brand-navy"
          testid="kpi-expected"
        />
        <KpiCard
          icon={TrendingDown}
          label="Fark"
          value={<span className={deltaClass(data?.total_delta || 0)}>{fmtSar(data?.total_delta)}</span>}
          sub={(data?.total_delta || 0) < 0 ? "Eksik tahsilat" : (data?.total_delta || 0) > 0 ? "Fazla tahsilat" : "Denk"}
          accent="brand-orange"
          testid="kpi-delta"
        />
        <KpiCard
          icon={Users}
          label="Saha Çalışanları"
          value={(data?.by_collector || []).length}
          sub="Ödeme yapan farklı kişi sayısı"
          accent="slate-600"
          testid="kpi-collectors"
        />
      </div>

      {/* By-collector summary */}
      {(data?.by_collector || []).length > 0 && (
        <div className="rounded-2xl bg-white border border-brand-navy/10 p-4" data-testid="collected-by-collector">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-serif text-lg text-brand-navy">Çalışan Bazında Özet</h2>
            <div className="text-[11px] text-slate-500">Yüksek tahsilattan düşüğe sıralı</div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {(data?.by_collector || []).map((c) => (
              <button
                key={c.collected_by}
                type="button"
                onClick={() => { setCollectorId(c.collected_by); load(); }}
                className={`text-left rounded-xl border p-3 hover:bg-canvas-cream/50 transition ${
                  collectorId === c.collected_by ? "border-brand-orange bg-brand-orange/5" : "border-slate-200"
                }`}
                data-testid={`collector-chip-${c.collected_by}`}
              >
                <div className="text-sm font-semibold text-brand-navy truncate">{c.collected_by_name || "—"}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{c.count} tahsilat</div>
                <div className="font-serif text-lg text-brand-orange-dark mt-1">{fmtSar(c.total)}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-white rounded-2xl border border-brand-navy/10 p-3 flex flex-wrap items-center gap-2" data-testid="collected-filters">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rez.No, grup, otel, saha çalışanı..." className="pl-8" data-testid="collected-search" />
        </div>
        <select value={collectorId} onChange={(e) => setCollectorId(e.target.value)} className="flex h-10 rounded-md border border-input bg-transparent px-3 text-sm" data-testid="collected-filter-collector">
          <option value="all">Tüm Saha Çalışanları</option>
          {collectorOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={hotelName} onChange={(e) => setHotelName(e.target.value)} className="flex h-10 rounded-md border border-input bg-transparent px-3 text-sm" data-testid="collected-filter-hotel">
          <option value="all">Tüm Oteller</option>
          {hotelOptions.map((h) => <option key={h} value={h}>{h}</option>)}
        </select>
        <div className="flex items-center gap-1 text-sm">
          <Calendar size={13} className="text-slate-400" />
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-10 w-[140px]" data-testid="collected-from" />
          <span className="text-slate-400">→</span>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-10 w-[140px]" data-testid="collected-to" />
        </div>
        <Button onClick={load} disabled={loading} className="bg-brand-navy text-white" data-testid="collected-apply">
          {loading ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Search size={13} className="mr-1" />}
          Uygula
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-white border border-brand-navy/10 overflow-hidden" data-testid="collected-table-wrap">
        {loading && (data?.items?.length || 0) === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <Loader2 size={20} className="mx-auto mb-2 animate-spin text-brand-orange" />
            Tahsilatlar yükleniyor…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="collected-empty">
            <div className="text-3xl mb-2">💰</div>
            Henüz kayıtlı tahsilat yok. Saha çalışanları müşterilerden para aldıktan sonra kayıtlar burada belirir.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-canvas-stone text-[11px] uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="text-left py-2 px-3 font-semibold">Tarih/Saat</th>
                  <th className="text-left py-2 px-3 font-semibold">Grup</th>
                  <th className="text-left py-2 px-3 font-semibold">Otel</th>
                  <th className="text-left py-2 px-3 font-semibold">Saha Çalışanı</th>
                  <th className="text-right py-2 px-3 font-semibold">Alınacak</th>
                  <th className="text-right py-2 px-3 font-semibold">Alınan</th>
                  <th className="text-right py-2 px-3 font-semibold">Fark</th>
                  <th className="text-left py-2 px-3 font-semibold">Oda No</th>
                  <th className="text-left py-2 px-3 font-semibold w-8"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id} className="border-t border-slate-100 hover:bg-canvas-cream/40" data-testid={`collected-row-${it.id}`}>
                    <td className="py-2 px-3 font-mono text-[12px] text-slate-600 whitespace-nowrap">{fmtStamp(it.collected_at)}</td>
                    <td className="py-2 px-3 min-w-[160px]">
                      <div className="font-medium text-brand-navy truncate">{it.group_name || <span className="italic text-slate-400">—</span>}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{it.reservation_number}</div>
                      {it.customer_phone && (
                        <a href={`tel:${it.customer_phone}`} className="text-[11px] text-brand-orange inline-flex items-center gap-1 hover:underline">
                          <Phone size={10} /> {it.customer_phone}
                        </a>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <div className="text-brand-navy inline-flex items-center gap-1"><Hotel size={12} className="text-slate-400" />{it.hotel_name || "—"}</div>
                      <div className="text-[10px] text-slate-500">{it.agency_name}</div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="text-brand-navy font-medium">{it.collected_by_name || "—"}</div>
                    </td>
                    <td className="py-2 px-3 text-right font-mono">{fmtSar(it.expected_fee)}</td>
                    <td className="py-2 px-3 text-right font-mono font-semibold text-emerald-700">{fmtSar(it.collected_amount)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${deltaClass(it.delta)}`}>{fmtSar(it.delta)}</td>
                    <td className="py-2 px-3 text-slate-700 whitespace-nowrap max-w-[140px] truncate" title={it.room_numbers}>
                      {it.room_numbers || <span className="italic text-slate-400">—</span>}
                    </td>
                    <td className="py-2 px-3">
                      <Link
                        to={`/acente/rezervasyon/${it.id}`}
                        className="text-brand-navy hover:text-brand-orange"
                        title="Rezervasyon detayı"
                        data-testid={`collected-open-${it.id}`}
                      >
                        <ArrowRight size={14} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-200 bg-canvas-cream/60 font-semibold text-sm">
                  <td colSpan={4} className="py-2 px-3 text-slate-700">Toplam ({filtered.length} kayıt)</td>
                  <td className="py-2 px-3 text-right font-mono">{fmtSar(filteredTotals.expected)}</td>
                  <td className="py-2 px-3 text-right font-mono text-emerald-700">{fmtSar(filteredTotals.collected)}</td>
                  <td className={`py-2 px-3 text-right font-mono ${deltaClass(filteredTotals.collected - filteredTotals.expected)}`}>
                    {fmtSar(filteredTotals.collected - filteredTotals.expected)}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
